import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IconButton, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import type { FC } from 'react';

import CircleLoader from 'components/CircleLoader/CircleLoader';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import { StatusIdE } from 'declarations/enums/tickets';
import type { AttachmentI } from 'declarations/interfaces/attachments';
import type { NoopT } from 'declarations/types/common';

import DeleteAttachmentPopup from './components/DeleteAttachmentPopup/DeleteAttachmentPopup';
import { ActionTypeE } from './declarations';
import useViewAttachmentsPopup from './hooks/useViewAttachmentsPopup';
import {
  AttachmentNameStyled,
  FlexStyled,
  LabelStyled,
  ListItemActionsRowStyled,
  ListItemMetaFieldStyled,
  ListItemMetaFieldTypeStyled,
  ListItemMetaFieldsStyled,
  ListItemStyled,
  ListItemTopRowStyled,
  ListStyled,
  ValueStyled,
} from './ViewAttachmentsPopup.styled';

export type ViewAttachmentsCardPropsT = {
  ticketId: string;
  ticketStatus: StatusIdE;
  isExpanded: boolean;
  attachments?: AttachmentI[] | null;
  isLoadingAttachments?: boolean;
  handleOpenDeleteAttachmentPopup?: (attachment: AttachmentI) => NoopT;
  handleCloseDeleteAttachmentPopup?: NoopT;
  selectedAttachment?: AttachmentI | null;
  handleAttachmentClick?: (attachment: AttachmentI, actionType: ActionTypeE) => NoopT;
  isLoadingAttachmentForPreview?: boolean;
  isLoadingAttachmentForDownload?: boolean;
  isAttachmentForDownloadError?: boolean;
  isAttachmentForPreviewError?: boolean;
  isDeleteAttachmentPopupOpened?: boolean;
};

const ViewAttachmentsCard: FC<ViewAttachmentsCardPropsT> = ({
  ticketId,
  ticketStatus,
  isExpanded: _isExpanded,
  attachments: attachmentsProp,
  isLoadingAttachments: isLoadingAttachmentsProp,
  handleOpenDeleteAttachmentPopup: handleOpenDeleteAttachmentPopupProp,
  handleCloseDeleteAttachmentPopup: handleCloseDeleteAttachmentPopupProp,
  selectedAttachment: selectedAttachmentProp,
  handleAttachmentClick: handleAttachmentClickProp,
  isLoadingAttachmentForPreview: isLoadingAttachmentForPreviewProp,
  isLoadingAttachmentForDownload: isLoadingAttachmentForDownloadProp,
  isAttachmentForDownloadError: isAttachmentForDownloadErrorProp,
  isAttachmentForPreviewError: isAttachmentForPreviewErrorProp,
  isDeleteAttachmentPopupOpened: isDeleteAttachmentPopupOpenedProp,
}) => {
  const hookResult = useViewAttachmentsPopup({ ticketId });
  const useParentState = attachmentsProp !== undefined;
  const attachments = useParentState ? attachmentsProp ?? null : hookResult.attachments;
  const isLoadingAttachments = useParentState
    ? isLoadingAttachmentsProp ?? false
    : hookResult.isLoadingAttachments;
  const handleOpenDeleteAttachmentPopup =
    (useParentState
      ? handleOpenDeleteAttachmentPopupProp
      : hookResult.handleOpenDeleteAttachmentPopup) ?? hookResult.handleOpenDeleteAttachmentPopup;
  const handleCloseDeleteAttachmentPopup =
    (useParentState
      ? handleCloseDeleteAttachmentPopupProp
      : hookResult.handleCloseDeleteAttachmentPopup) ?? hookResult.handleCloseDeleteAttachmentPopup;
  const selectedAttachment = useParentState
    ? selectedAttachmentProp
    : hookResult.selectedAttachment;
  const handleAttachmentClick =
    (useParentState ? handleAttachmentClickProp : hookResult.handleAttachmentClick) ??
    hookResult.handleAttachmentClick;
  const isLoadingAttachmentForPreview = useParentState
    ? isLoadingAttachmentForPreviewProp ?? false
    : hookResult.isLoadingAttachmentForPreview;
  const isLoadingAttachmentForDownload = useParentState
    ? isLoadingAttachmentForDownloadProp ?? false
    : hookResult.isLoadingAttachmentForDownload;
  const isAttachmentForDownloadError = useParentState
    ? isAttachmentForDownloadErrorProp ?? false
    : hookResult.isAttachmentForDownloadError;
  const isAttachmentForPreviewError = useParentState
    ? isAttachmentForPreviewErrorProp ?? false
    : hookResult.isAttachmentForPreviewError;
  const isDeleteAttachmentPopupOpened = useParentState
    ? isDeleteAttachmentPopupOpenedProp ?? false
    : hookResult.isDeleteAttachmentPopupOpened;

  const isAttachmentsFetchingError = useParentState ? false : hookResult.isAttachmentsFetchingError;

  return (
    <>
      {!!attachments?.length && !isLoadingAttachments && (
        <FlexContainer direction="column" align="stretch">
          <ListStyled>
            {attachments?.map((attachment, i) => {
              const isPreviewActive =
                isLoadingAttachmentForPreview &&
                selectedAttachment?.fileName === attachment.fileName;
              const isDownloadActive =
                isLoadingAttachmentForDownload &&
                selectedAttachment?.fileName === attachment.fileName;
              const isDeleteBtnDisabled = ticketStatus !== StatusIdE.Open;
              return (
                <ListItemStyled key={`attachment-${i}`}>
                  <ListItemTopRowStyled>
                    <AttachmentNameStyled>
                      {attachment.originalName !== undefined
                        ? attachment.originalName
                        : attachment.fileName}
                    </AttachmentNameStyled>
                  </ListItemTopRowStyled>
                  <ListItemActionsRowStyled>
                    <Tooltip title="View" arrow>
                      <IconButton
                        aria-label="View"
                        disabled={isPreviewActive}
                        onClick={handleAttachmentClick(attachment, ActionTypeE.View)}
                        sx={{ color: (t): string => t.palette.primary.main }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download" arrow>
                      <IconButton
                        aria-label="Download"
                        disabled={isDownloadActive}
                        onClick={handleAttachmentClick(attachment, ActionTypeE.Download)}
                        sx={{ color: (t): string => t.palette.primary.main }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton
                        aria-label="Delete"
                        disabled={isDeleteBtnDisabled}
                        onClick={handleOpenDeleteAttachmentPopup(attachment)}
                        sx={{ color: (t): string => t.palette.primary.main }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <ListItemMetaFieldsStyled>
                      <ListItemMetaFieldTypeStyled>
                        <LabelStyled size="xs">Type</LabelStyled>
                        <Tooltip title={attachment.attachmentTypeCode || ''} arrow>
                          <span>
                            <ValueStyled color="primaryText" size="xs">
                              {attachment.attachmentTypeCode}
                            </ValueStyled>
                          </span>
                        </Tooltip>
                      </ListItemMetaFieldTypeStyled>
                      <ListItemMetaFieldStyled>
                        <LabelStyled size="xs">Uploaded</LabelStyled>
                        <ValueStyled color="primaryText" size="xs">
                          {format(new Date(attachment.createdDateTime), 'MMM d, yyyy')}
                        </ValueStyled>
                      </ListItemMetaFieldStyled>
                    </ListItemMetaFieldsStyled>
                  </ListItemActionsRowStyled>
                </ListItemStyled>
              );
            })}
          </ListStyled>
        </FlexContainer>
      )}

      {(isAttachmentsFetchingError ||
        isAttachmentForDownloadError ||
        isAttachmentForPreviewError) && (
        <FlexContainer direction="column">
          <ErrorSection />
        </FlexContainer>
      )}

      {isLoadingAttachments && (
        <FlexStyled align="center" justify="center">
          <CircleLoader />
        </FlexStyled>
      )}

      {!useParentState && (
        <DeleteAttachmentPopup
          ticketId={ticketId}
          fileName={selectedAttachment?.fileName || ''}
          originalName={
            (selectedAttachment?.originalName !== undefined
              ? selectedAttachment?.originalName
              : selectedAttachment?.fileName) || ''
          }
          isOpen={isDeleteAttachmentPopupOpened}
          onClose={handleCloseDeleteAttachmentPopup}
        />
      )}
    </>
  );
};

export default ViewAttachmentsCard;
